// File: frontend/src/components/common/PullToRefresh.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaArrowDown, FaSyncAlt } from "react-icons/fa";
import { useIsPwaStandalone } from "../../hooks/useIsPwaStandalone";
import "./PullToRefresh.css";

interface PullToRefreshProps {
  /** Called when the user pulls past the threshold and releases. */
  onRefresh: () => void | Promise<void>;
  /**
   * Set false to disable the gesture on pages where it would fight with their
   * own scrolling (admin, messages).
   */
  enabled?: boolean;
}

// How far the finger must travel before a release triggers the refresh.
const TRIGGER_DISTANCE_PX = 70;

// Hard cap on the indicator travel, so a long drag does not push the strip
// halfway down the screen.
const MAX_PULL_PX = 110;

// Drag beyond the threshold moves the indicator at a fraction of finger speed,
// which is what makes the gesture feel like it is resisting.
const RESISTANCE = 0.45;

/**
 * Pull-to-refresh for the installed PWA.
 *
 * An installed iOS app has no browser chrome, so the native "drag down to
 * reload" affordance is gone — this restores it. It is deliberately limited to
 * standalone mode: inside a normal tab the browser already provides the
 * gesture and two overlapping handlers would fight each other.
 *
 * The app does not scroll the document; each pane (.left-pane, .main-content)
 * scrolls internally. So instead of watching window.scrollY the handler walks
 * up from the touched element to its nearest scrollable ancestor and only arms
 * when that element is already at the top.
 */
export const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, enabled = true }) => {
  const isStandalone = useIsPwaStandalone();
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Kept in refs rather than state: these change on every touchmove and must
  // not queue a React render per frame.
  const startYRef = useRef(0);
  const isArmedRef = useRef(false);
  const isRefreshingRef = useRef(false);

  // The caller passes a fresh function on every render (it cannot be memoised
  // there without breaking rules-of-hooks). Holding it in a ref keeps
  // runRefresh stable, so the touch listeners below are attached once instead
  // of being torn down and re-registered on each render.
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const isActive = isStandalone && enabled;

  const runRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    setIsRefreshing(true);
    setPullDistance(TRIGGER_DISTANCE_PX);
    try {
      await onRefreshRef.current();
    } finally {
      // Brief hold so the spinner is visible even when the refresh returns
      // almost immediately from cache — an indicator that flashes for one
      // frame reads as "nothing happened".
      window.setTimeout(() => {
        isRefreshingRef.current = false;
        setIsRefreshing(false);
        setPullDistance(0);
      }, 400);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const handleTouchStart = (event: TouchEvent) => {
      if (isRefreshingRef.current || event.touches.length !== 1) {
        isArmedRef.current = false;
        return;
      }
      const scroller = findScrollableAncestor(event.target as Element | null);
      // scrollTop can go slightly negative during iOS rubber-banding, so the
      // check is <= rather than === 0.
      const atTop = scroller ? scroller.scrollTop <= 0 : window.scrollY <= 0;
      isArmedRef.current = atTop;
      startYRef.current = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isArmedRef.current || isRefreshingRef.current) return;

      const delta = event.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        // Scrolling up again — hand control back to the pane.
        setPullDistance(0);
        isArmedRef.current = false;
        return;
      }

      // Stops the pane from scrolling while the indicator is being dragged.
      // Requires the listener to be registered with passive: false.
      if (event.cancelable) event.preventDefault();

      setPullDistance(Math.min(delta * RESISTANCE, MAX_PULL_PX));
    };

    const handleTouchEnd = () => {
      if (!isArmedRef.current || isRefreshingRef.current) {
        setPullDistance(0);
        return;
      }
      isArmedRef.current = false;
      setPullDistance((current) => {
        if (current >= TRIGGER_DISTANCE_PX) {
          void runRefresh();
          return current;
        }
        return 0;
      });
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isActive, runRefresh]);

  if (!isActive || pullDistance <= 0) return null;

  const hasReachedThreshold = pullDistance >= TRIGGER_DISTANCE_PX;

  return (
    <div
      className="ptr-indicator"
      style={{ transform: `translateY(${pullDistance}px)` }}
      aria-hidden="true"
    >
      <div className={`ptr-circle${hasReachedThreshold ? " ptr-circle--ready" : ""}`}>
        {isRefreshing ? (
          <FaSyncAlt className="ptr-icon ptr-icon--spinning" />
        ) : (
          <FaArrowDown
            className="ptr-icon"
            style={{ transform: hasReachedThreshold ? "rotate(180deg)" : "none" }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Walks up from the touched node to the first ancestor that actually scrolls.
 * Returns null when nothing in the chain scrolls, in which case the caller
 * falls back to the window position.
 */
function findScrollableAncestor(element: Element | null): Element | null {
  let node: Element | null = element;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const canScroll = overflowY === "auto" || overflowY === "scroll";
    if (canScroll && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}